export const backgroundColorsMap = {
  primary: 'bg-background-primary',
  secondary: 'bg-background-secondary',
  dark: 'bg-background-dark',
  lilac: 'bg-background-lilac',
  pinkCandy: 'bg-background-pink-candy',
  redCandy: 'bg-background-red-candy',
  stone: 'bg-background-stone',
  mint: 'bg-background-mint',
  oceanFoam: 'bg-background-ocean-foam',
  salmon: 'bg-background-salmon',
  sun: 'bg-background-sun',
  blueExtreme: 'bg-background-blue-extreme',
  lilacExtreme: 'bg-background-lilac-extreme',
} as const;

export const backgroundColorsValueMap: Record<
  keyof typeof backgroundColorsMap,
  string
> = {
  primary: '#FFFFFF',
  secondary: '#F9F7F6',
  dark: '#171D2D',
  lilac: '#EDE7F3',
  pinkCandy: '#F7E4F2',
  redCandy: '#FAEAE9',
  stone: '#F0EEEA',
  mint: '#E8EFE0',
  oceanFoam: '#E2E5F5',
  salmon: '#FFD9B7',
  sun: '#FFEBB7',
  blueExtreme: '#343294',
  lilacExtreme: '#513294',
} as const;

export type CustomBackgroundColor = keyof typeof backgroundColorsMap;

export const colorsList = ['light', 'dark'] as const;

export type CustomColor = (typeof colorsList)[number];
export const PRIMARY_COLOR = '#FFD689';

// prettier-ignore
const colorNames = ['accent', 'gray', 'mauve', 'slate', 'sage', 'olive', 'sand', 'tomato', 'red', 'ruby', 'crimson', 'pink', 'plum', 'purple', 'violet', 'iris', 'indigo', 'blue', 'cyan', 'teal', 'jade', 'green', 'grass', 'bronze', 'gold', 'brown', 'orange', 'amber', 'yellow', 'lime', 'mint', 'sky'] as const

type ScaleStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type SolidColorScale = Record<ScaleStep, string>;
type AlphaColorScale = Record<`a${ScaleStep}`, string>;
type FunctionalScale = Record<
  'surface' | 'indicator' | 'track' | 'contrast',
  string
>;

type PrimaryColor = (typeof colorNames)[number];
type ColorName = PrimaryColor | 'black' | 'white';

type RadixColors = Record<
  PrimaryColor,
  SolidColorScale & AlphaColorScale & FunctionalScale
> & {
  black: AlphaColorScale;
  white: AlphaColorScale;
};

function createSolidScale(name: ColorName) {
  return Array.from({ length: 12 }).map((_, i) => [
    i + 1,
    `var(--${name}-${i + 1})`,
  ]);
}

function createAlphaScale(name: ColorName) {
  return Array.from({ length: 12 }).map((_, i) => [
    `a${i + 1}`,
    `var(--${name}-a${i + 1})`,
  ]);
}

function createFunctionalSteps(name: ColorName) {
  return ['surface', 'indicator', 'track', 'contrast'].map((key) => [
    key,
    `var(--${name}-${key})`,
  ]);
}

function createRadixColors() {
  const colors = {} as RadixColors;

  for (const name of colorNames) {
    colors[name] = Object.fromEntries([
      ...createSolidScale(name),
      ...createAlphaScale(name),
      ...createFunctionalSteps(name),
    ]);
  }

  colors.black = Object.fromEntries([
    ...createAlphaScale('black'),
    ['DEFAULT', '#000000'],
  ]);
  colors.white = Object.fromEntries([
    ...createAlphaScale('white'),
    ['DEFAULT', '#FFFFFF'],
  ]);

  return colors;
}

const colors = {
  css: createRadixColors(),
};

export default colors;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const WAVE_COLORS_1 = ['#eab308', '#f59e0b', '#fde047', '#facc15', '#fef9c3'];
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const WAVE_COLORS_2 = [
  '#b78b07ff',
  '#c17d08ff',
  '#fcd305ff',
  '#d1a804ff',
  '#fdf066ff',
];

// export const PRIMARY_BACKGROUND_COLOR = '#030713';
export const PRIMARY_BACKGROUND_COLOR = '#030713';

/**
 * Use for more greyish background
 */
export const DARK_BACKGROUND_COLOR = '#0D0D0D';

const lightBlue = '#0790EB';
const mediumBlue = '#0A68F5';
const skyBlue = '#4795FC';
const darkBlue = '#1F14FA';
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const paleBlue_1 = '#C4D7FF';
const paleBlue_2 = '#5ebdff';

function hexToGrayscale(hex: string): string {
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex)) {
    throw new Error(`Invalid hex color string - ${hex}`);
  }

  const rgb = hex
    .replace('#', '')
    .match(/.{1,2}/g)
    ?.map((value) => Number.parseInt(value, 16));

  if (!rgb) return hex;

  const grayscale = Math.round(
    // @ts-expect-error - should be defined
    0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2],
  );

  const grayscaleHex = grayscale.toString(16).padStart(2, '0');
  return `#${grayscaleHex}${grayscaleHex}${grayscaleHex}`;
}

export const WAVE_COLORS_4 = [
  lightBlue,
  mediumBlue,
  skyBlue,
  darkBlue,
  paleBlue_2,
];

export const WAVE_COLORS_5 = [
  '#FFFFFF', // white
  '#D3D3D3', // light gray 1
  '#C0C0C0', // light gray 2
  '#c9c9c9', // light gray 3
  '#c4c4c4', // light gray 4
];

export const WAVE_COLORS_3 = WAVE_COLORS_4.map((color) =>
  hexToGrayscale(color),
);
